import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRightIcon, LoaderIcon } from 'lucide-react';
const WAITLIST_URL = 'https://script.google.com/macros/s/AKfycbwh0J9Vkq6zT5v5-Pe-5GZczrP-7RSoI2ANR30Epc1TUMBa6CtWBK-Hu7W5Kn1EZVlOfw/exec';
export function FinalCTA() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email) return;
    setStatus('loading');
    try {
      await fetch(WAITLIST_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name,
          email
        })
      });
      setStatus('success');
      setName('');
      setEmail('');
      setTimeout(() => setStatus('idle'), 5000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };
  return <section id="waitlist" className="relative py-24 lg:py-32 bg-gray-950 overflow-hidden" data-id="element-15">
      {/* Background glow */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(251,191,36,0.08),transparent_60%)]" data-id="element-16" />

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-17">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.5
      }} data-id="element-18">
          <img src="https://cdn.magicpatterns.com/uploads/eVLeSDgeNsVguecVaW2uFB/LOGO_white_-_.png" alt="KittyPot logo" className="w-32 h-32 object-contain rounded-full mx-auto mb-6" data-id="element-19" />
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight mb-5" data-id="element-20">
            Stop chasing.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-kitty-yellow-400 to-orange-400" data-id="element-21">
              Start splitting.
            </span>
          </h2>
          <p className="text-lg lg:text-xl text-gray-400 mb-10 max-w-lg mx-auto" data-id="element-22">
            Free to join. Takes 30 seconds. Limited spots.
          </p>

          {status === 'success' ? <motion.div initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-lg mx-auto mb-6 bg-green-500/10 border border-green-500/20 rounded-2xl px-6 py-4 flex items-center justify-center gap-3" data-id="element-23">
              <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" data-id="element-24" />
              <div className="text-left" data-id="element-25">
                <p className="text-green-300 font-semibold" data-id="element-26">
                  You're on the waitlist 🎉
                </p>
                <p className="text-green-400/60 text-sm mt-1" data-id="element-27">
                  We'll let you know as soon as KittyPot is ready. Early users
                  get first access.
                </p>
              </div>
            </motion.div> : <form onSubmit={handleSubmit} className="max-w-lg mx-auto mb-6 space-y-4" data-id="element-28">
              <input type="text" placeholder="Full name" value={name} onChange={e => setName(e.target.value)} className="w-full pl-5 pr-5 py-[14px] lg:py-4 min-h-[52px] rounded-full border border-white/20 bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-kitty-yellow focus:border-transparent text-white placeholder-gray-400 text-base backdrop-blur-sm" required data-id="element-29" />
              <input type="email" placeholder="Enter your email" value={email} onChange={e => setEmail(e.target.value)} className="w-full pl-5 pr-5 py-[14px] lg:py-4 min-h-[52px] rounded-full border border-white/20 bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-kitty-yellow focus:border-transparent text-white placeholder-gray-400 text-base backdrop-blur-sm" required data-id="element-30" />
              <button type="submit" disabled={status === 'loading'} className="w-full bg-kitty-yellow hover:bg-kitty-yellow-500 text-kitty-dark px-8 py-[16px] lg:py-4 min-h-[56px] rounded-full font-bold transition-all active:scale-[0.98] shadow-lg hover:shadow-xl flex items-center justify-center gap-2 text-base disabled:opacity-70 disabled:cursor-not-allowed mt-2" data-id="element-31">
                {status === 'loading' ? <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-32" /> : <>
                    Get Early Access
                    <ArrowRightIcon className="w-4 h-4" data-id="element-33" />
                  </>}
              </button>
              {status === 'error' && <p className="text-red-400 text-sm" data-id="element-34">
                  Something went wrong. Please try again.
                </p>}
            </form>}

          {/* Early access positioning */}
          <div className="flex flex-col items-center gap-4" data-id="element-35">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.06] border border-white/[0.1]" data-id="element-36">
              <span className="relative flex h-2 w-2" data-id="element-37">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-38" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" data-id="element-39" />
              </span>
              <p className="text-sm font-semibold text-white" data-id="element-40">
                Early access now open
              </p>
            </div>
            <p className="text-sm text-gray-500" data-id="element-41">
              Free to join · No commitment · Limited spots
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}